import { useEffect, useState } from "react";
import { customerAPI, licenseAPI } from "../services/api";
import Popup from "./Popup";
import DataTable from "./DataTable";
import StatusBadge from "./StatusBadge";
import RenewLicenseModal from "./RenewLicenseModal";
import LicenseKeyModal from "./LicenseKeyModal";
import FilledButton from "./FilledButton";
import {
  Block as BlockIcon,
  Refresh as RenewIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { format, parseISO, isValid } from "date-fns";

export default function LicenseHistoryModal({
  isOpen,
  onClose,
  customer,
  onRenewLicense,
  onRevokeLicense,
}) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showLicenseKeyModal, setShowLicenseKeyModal] = useState(false);
  const [renewingLicense, setRenewingLicense] = useState(null);
  const [generatedLicenseKey, setGeneratedLicenseKey] = useState("");
  const [generatedLicenseId, setGeneratedLicenseId] = useState("");

  useEffect(() => {
    if (isOpen && customer?.id) {
      loadLicenses();
    }
  }, [isOpen, customer?.id]);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const data = await customerAPI.getLicenses(customer.id);
      setLicenses(data);
    } catch (error) {
      toast.error("Failed to load license history");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (licenseId) => {
    if (!window.confirm("Are you sure you want to revoke this license?")) {
      return;
    }

    try {
      await licenseAPI.revoke(licenseId, "Revoked by admin");
      toast.success("License revoked successfully!");
      loadLicenses();
      if (onRevokeLicense) onRevokeLicense();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to revoke license");
    }
  };

  const handleRenew = async (license) => {
    setRenewingLicense(license);
    setShowRenewModal(true);
  };

  const handleRenewSubmit = async (formData) => {
    try {
      const response = await licenseAPI.renew(
        renewingLicense.licenseId,
        formData
      );
      setGeneratedLicenseKey(response.licenseKey);
      setGeneratedLicenseId(response.licenseId);
      setShowRenewModal(false);
      setShowLicenseKeyModal(true);
      setRenewingLicense(null);
      loadLicenses(); // Refresh license list
      if (onRenewLicense) onRenewLicense();
      toast.success("License renewed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to renew license");
    }
  };

  const handleGenerateNewLicense = () => {
    if (licenses.length === 0) {
      toast.error("No license found to renew");
      return;
    }

    const canRenewLicense = (license) => {
      return (
        license.status !== "revoked" ||
        (license.status === "revoked" && license.renewedToLicenseId)
      );
    };
    let licenseToRenew = licenses.find(canRenewLicense);
    if (!licenseToRenew) {
      licenseToRenew = licenses[0];
    }
    if (licenseToRenew) {
      handleRenew(licenseToRenew);
    } else {
      toast.error("Cannot generate new license. No valid license found.");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleCloseLicenseKeyModal = () => {
    setShowLicenseKeyModal(false);
    setGeneratedLicenseKey("");
    setGeneratedLicenseId("");
  };

  // Check if customer has any active licenses
  const hasActiveLicense = licenses.some(
    (license) => license.status === "activated" || license.status === "pending"
  );

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case "basic":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "whatsapp":
        return "bg-green-50 text-green-700 border-green-200";
      case "ai_only":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "ai_and_whatsapp":
        return "bg-teal-50 text-teal-700 border-teal-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      let date;
      if (typeof dateString === "string") {
        const parsed = parseISO(dateString);
        date = isValid(parsed) ? parsed : new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      if (!isValid(date)) return "—";
      return format(date, "MMM dd, yyyy");
    } catch (error) {
      return "—";
    }
  };

  const columns = [
    {
      key: "licenseId",
      label: "License ID",
      path: "licenseId",
    },
    {
      key: "tier",
      label: "Tier",
      path: "tier",
    },
    {
      key: "status",
      label: "Status",
      path: "status",
    },
    {
      key: "issuedAt",
      label: "Issued",
      path: "issuedAt",
    },
    {
      key: "expiresAt",
      label: "Expires",
      path: "expiresAt",
    },
    {
      key: "machineId",
      label: "Machine ID",
      path: "machineIdHash",
    },
    {
      key: "activationIp",
      label: "Activation IP",
      path: "activationIp",
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  const customRenderers = {
    tier: (value) => (
      <span
        className={`px-2.5 py-1 text-xs font-medium rounded border capitalize ${getTierColor(
          value
        )}`}
      >
        {value || "—"}
      </span>
    ),
    status: (value) => <StatusBadge status={value} />,
    issuedAt: (value) => (
      <div className="text-sm font-medium text-gray-900">
        {formatDate(value)}
      </div>
    ),
    expiresAt: (value) => (
      <div className="text-sm font-medium text-gray-900">
        {formatDate(value)}
      </div>
    ),
    machineId: (value) => {
      if (!value) return <span className="text-sm text-gray-400">—</span>;
      const displayValue =
        value.length > 16 ? `${value.substring(0, 16)}...` : value;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(value);
          }}
          className="text-sm text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
          title="Click to copy full machine ID"
        >
          {displayValue}
        </button>
      );
    },
    activationIp: (value) => {
      if (!value) return <span className="text-sm text-gray-400">—</span>;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(value);
          }}
          className="text-sm text-gray-700 font-mono hover:text-primaryGreen transition-colors cursor-pointer"
          title="Click to copy IP address"
        >
          {value}
        </button>
      );
    },
    actions: (value, item) => (
      <div className="flex items-center gap-2">
        {item.status !== "revoked" && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRenew(item);
              }}
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5 transition-colors text-sm"
              title="Renew License"
            >
              <RenewIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRevoke(item.licenseId);
              }}
              className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors text-sm"
              title="Revoke License"
            >
              <BlockIcon className="w-4 h-4" />
            </button>
          </>
        )}
        {item.status === "revoked" && (
          <span className="text-gray-400 text-xs">No actions</span>
        )}
      </div>
    ),
  };

  if (!isOpen) return null;

  return (
    <Popup
      popupTitle={`License History - ${customer?.name || customer?.email}`}
      onClose={onClose}
      width="max-w-6xl"
      increasePopupHeight={true}
    >
      <div className="flex flex-col gap-4 h-full min-h-0">
        {/* Generate New License Button - Renews the last license */}
        {licenses.length > 0 && (
          <div className="flex-shrink-0">
            <FilledButton
              text="Generate New License"
              width="w-full"
              IconFirst={AddIcon}
              btnClicked={handleGenerateNewLicense}
              textColor="white"
            />
          </div>
        )}

        {/* License History Table */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <DataTable
            data={licenses}
            columns={columns}
            isLoading={loading}
            itemName="license"
            fieldMappings={{
              dateFields: ["issuedAt", "expiresAt", "activatedAt"],
              statusFields: ["status"],
              actionFields: ["actions"],
            }}
            customRenderers={customRenderers}
          />
        </div>
      </div>

      {/* Renew Modal */}
      <RenewLicenseModal
        isOpen={showRenewModal}
        onClose={() => {
          setShowRenewModal(false);
          setRenewingLicense(null);
        }}
        onSubmit={handleRenewSubmit}
        license={renewingLicense}
      />

      {/* License Key Modal */}
      <LicenseKeyModal
        isOpen={showLicenseKeyModal}
        onClose={handleCloseLicenseKeyModal}
        licenseKey={generatedLicenseKey}
        licenseId={generatedLicenseId}
        onCopyToClipboard={copyToClipboard}
      />
    </Popup>
  );
}
